import { useEffect, useState } from "react";
import logo from "../images/logo-2.png";
import bg from "../images/bg-4.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons/faEyeSlash";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/AuthContext";
import SignIn from "../services/AuthService";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberme, setRememberme] = useState(false);
  const [hide, setHide] = useState(true);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const { login: contextLogin, isLoggedIn } = useAuth();

  useEffect(() => {
    // If the current path is not '/', redirect to '/'
    if (location.pathname !== "/") {
      navigate("/", { replace: true, state: { from: location } });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (isLoggedIn) {
      const from = location.state?.from?.pathname || "/ranking";
      navigate(from, { replace: true });
    }
  }, [isLoggedIn, navigate, location]);

  const handleRemember = () => {
    setRememberme((pre) => !pre);
  };

  const togglePasswordVisibility = () => {
    setHide((prevHide) => !prevHide);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const response = await SignIn(email, password);
    setIsLoading(false);
    if (response.success) {
      await contextLogin(response?.token, rememberme);
    } else {
      setMessage(response.message);
      setPassword("");
    }
  };

  return (
    <div className="flex flex-col md:flex-row-reverse h-screen w-screen bg-white text-gray-800 dark:bg-gray-900 dark:text-white">
      {/* Background Image Section */}
      <div
        className="w-full md:w-1/2 md:h-full h-2/5 flex justify-center items-center"
        style={{
          backgroundImage: `url(${bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
          textAlign: "center",
          fontSize: "24px",
        }}
      >
        AMAC SPORT
      </div>

      {/* Form Section */}
      <div className="flex flex-col justify-center items-center bg-gray-100 dark:bg-gray-800 md:px-36 md:py-7 md:w-1/2 h-3/5 md:h-full">
        <form className="flex flex-col" onSubmit={handleLogin}>
          <div className="flex flex-col justify-center items-center mb-4">
            <img alt="logo" src={logo} className="pb-10" />
            <h3 className="font-semibold text-2xl text-center">
              Đăng nhập vào AMACSPORT
            </h3>
          </div>
          <div className="justify-center flex flex-col items-center">
            <div className="mb-2 w-80 md:w-96">
              <input
                className="px-5 py-2 w-full text-gray-700 bg-white border border-gray-200 rounded focus:border-purple-200 focus:ring-2 focus:ring-purple-200 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                type="text"
                id="email"
                placeholder="Tài khoản"
                aria-label="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mt-2 relative w-80 md:w-96">
              <input
                className="px-5 py-2 w-full text-gray-700 bg-white border border-gray-200 rounded focus:border-purple-200 focus:ring-2 focus:ring-purple-200 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                type={hide ? "password" : "text"}
                id="password"
                placeholder="Mật khẩu"
                aria-label="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span
                className="absolute top-1/2 transform -translate-y-1/2 right-3 flex items-center cursor-pointer text-gray-500"
                onClick={togglePasswordVisibility}
              >
                {hide ? (
                  <FontAwesomeIcon icon={faEyeSlash} color="#9ca3af" />
                ) : (
                  <FontAwesomeIcon color="#9ca3af" icon={faEye} />
                )}
              </span>
            </div>
          </div>
          <div className="flex justify-start items-start pt-2">
            <input
              checked={rememberme}
              id="remember"
              type="checkbox"
              onChange={handleRemember}
              className="w-4 h-4 text-purple-600 hover:cursor-pointer bg-white border-gray-300 rounded focus:ring-purple-300 focus:ring-2 focus:outline-none checked:bg-white checked:border-purple-600 checked:text-purple-500 transition-all duration-200 ease-in-out dark:bg-gray-600"
            />
            <label
              htmlFor="remember"
              className="ml-2 text-sm cursor-pointer hover:text-purple-400 dark:text-gray-300"
            >
              Remember Me
            </label>
          </div>
          {message && <h3 className="text-red-600 text-left">{message}</h3>}
          <button
            className="text-white mt-3 cursor-pointer font-normal text-base bg-[#9816f4] border-[#9816f4] transition duration-150 ease-in-out focus:text-purple-600 focus:bg-white focus:border-purple-200 focus:shadow-xl hover:shadow-purple-500/50 hover:shadow-lg rounded-3xl mx-28 dark:bg-purple-600"
            type="submit"
          >
            {isLoading ? (
              <svg
                aria-hidden="true"
                className="inline w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-yellow-500"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            ) : (
              "Đăng Nhập"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
